import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgFor, NgIf, NgClass, AsyncPipe, DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject, Subscription, interval } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { AppState } from '../../state/reducers';
import * as VeridionExportActions from '../../state/veridionExport/veridion-export.actions';
import * as VeridionExportSelectors from '../../state/veridionExport/veridion-export.selectors';
import { VeridionExport, ExportListPagination } from '../../services/veridion-export.service';
import * as SearchExportActions from '../../state/searchExport/search-export.actions';
import * as SearchExportSelectors from '../../state/searchExport/search-export.selectors';
import { SearchExport, SearchExportListPagination } from '../../services/search-export.service';
import { HeaderService } from '../../services/header.service';
import { LeadExport, ProspectingService, isLeadExportActive, saveBlob } from '../../services/prospecting.service';
import { PopupService } from '../../services/popup.service';
import { ContinueExportDialogComponent } from '../leads/components/continue-export-dialog/continue-export-dialog.component';

/** Refresh cadence of the Leads tab while an export is running. */
const LEAD_EXPORTS_POLL_MS = 5000;

type ExportsTab = 'prospecting' | 'reports' | 'leads';

@Component({
  selector: 'app-exports',
  standalone: true,
  imports: [NgFor, NgIf, NgClass, AsyncPipe, DatePipe, DecimalPipe, RouterModule, FormsModule, ContinueExportDialogComponent],
  templateUrl: './exports.component.html',
  styleUrl: './exports.component.scss'
})
export class ExportsComponent implements OnInit, OnDestroy {
  // Tab state
  activeTab: ExportsTab = 'prospecting';

  // Lead exports ("Proceed to Leads" → Export to Excel)
  leadExports: LeadExport[] = [];
  leadExportsLoading = false;
  leadExportsLoaded = false;
  leadExportsError: string | null = null;
  leadExportsPagination = { total: 0, per_page: 15, current_page: 1, last_page: 1 };
  leadExportDownloadingIds = new Set<number>();
  /** Export being continued (out-of-credits stop), shown in the continue dialog. */
  continuingLeadExport: LeadExport | null = null;
  private leadExportsPollSub: Subscription | null = null;

  // Veridion (Advanced Prospecting) exports
  exports$: Observable<VeridionExport[]>;
  loading$: Observable<boolean>;
  error$: Observable<string | null>;
  pagination$: Observable<ExportListPagination>;
  downloadingIds$: Observable<number[]>;

  // Search Report exports
  searchExports$: Observable<SearchExport[]>;
  searchExportsLoading$: Observable<boolean>;
  searchExportsError$: Observable<string | null>;
  searchExportsPagination$: Observable<SearchExportListPagination>;
  searchExportDownloadingIds$: Observable<number[]>;

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private headerService: HeaderService,
    private route: ActivatedRoute,
    private prospectingService: ProspectingService,
    private popupService: PopupService
  ) {
    // Veridion selectors
    this.exports$ = this.store.select(VeridionExportSelectors.selectExports);
    this.loading$ = this.store.select(VeridionExportSelectors.selectExportsLoading);
    this.error$ = this.store.select(VeridionExportSelectors.selectExportsError);
    this.pagination$ = this.store.select(VeridionExportSelectors.selectExportsPagination);
    this.downloadingIds$ = this.store.select(VeridionExportSelectors.selectDownloadingIds);

    // Search Export selectors
    this.searchExports$ = this.store.select(SearchExportSelectors.selectSearchExports);
    this.searchExportsLoading$ = this.store.select(SearchExportSelectors.selectSearchExportsLoading);
    this.searchExportsError$ = this.store.select(SearchExportSelectors.selectSearchExportsError);
    this.searchExportsPagination$ = this.store.select(SearchExportSelectors.selectSearchExportsPagination);
    this.searchExportDownloadingIds$ = this.store.select(SearchExportSelectors.selectSearchExportDownloadingIds);
  }

  ngOnInit(): void {
    this.headerService.setHeaderState('Export', null, true);

    // ?tab=leads deep link (e.g. from the Leads page / export notifications)
    if (this.route.snapshot.queryParamMap.get('tab') === 'leads') {
      this.activeTab = 'leads';
      this.loadLeadExports();
    } else {
      this.loadExports();
    }
  }

  ngOnDestroy(): void {
    this.stopLeadExportsPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }

  switchTab(tab: ExportsTab): void {
    this.activeTab = tab;
    if (tab !== 'leads') {
      this.stopLeadExportsPolling();
    }
    if (tab === 'prospecting') {
      this.loadExports();
    } else if (tab === 'leads') {
      this.loadLeadExports();
    } else {
      this.loadSearchExports();
    }
  }

  onTabSelectChange(): void {
    this.switchTab(this.activeTab);
  }

  // ============================================
  // Veridion (Advanced Prospecting) methods
  // ============================================

  loadExports(): void {
    this.store.dispatch(VeridionExportActions.loadExports({
      page: 1
    }));
  }

  onPageChange(page: number): void {
    this.store.dispatch(VeridionExportActions.loadExports({
      page
    }));
  }

  refreshExports(): void {
    if (this.activeTab === 'prospecting') {
      this.loadExports();
    } else if (this.activeTab === 'leads') {
      this.loadLeadExports(this.leadExportsPagination.current_page);
    } else {
      this.loadSearchExports();
    }
  }

  downloadExport(exportItem: VeridionExport): void {
    this.store.dispatch(VeridionExportActions.downloadExport({
      exportId: exportItem.id,
      fileName: exportItem.file_name
    }));
  }

  isDownloading(exportId: number, downloadingIds: number[]): boolean {
    return downloadingIds.includes(exportId);
  }

  // ============================================
  // Search Report Export methods
  // ============================================

  loadSearchExports(): void {
    this.store.dispatch(SearchExportActions.loadSearchExports({
      page: 1
    }));
  }

  onSearchExportPageChange(page: number): void {
    this.store.dispatch(SearchExportActions.loadSearchExports({
      page
    }));
  }

  downloadSearchExport(exportItem: SearchExport): void {
    this.store.dispatch(SearchExportActions.downloadSearchExport({
      exportId: exportItem.id,
      fileName: exportItem.file_name
    }));
  }

  isSearchExportDownloading(exportId: number, downloadingIds: number[]): boolean {
    return downloadingIds.includes(exportId);
  }

  // ============================================
  // Lead exports (prospecting lists → Excel)
  // ============================================

  loadLeadExports(page = 1, quiet = false): void {
    if (!quiet) {
      this.leadExportsLoading = true;
    }
    this.leadExportsError = null;

    this.prospectingService.listLeadExports(page, this.leadExportsPagination.per_page)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ data, metadata }) => {
          this.leadExports = data;
          this.leadExportsPagination = {
            total: metadata.total ?? data.length,
            per_page: metadata.per_page ?? this.leadExportsPagination.per_page,
            current_page: metadata.current_page ?? page,
            last_page: metadata.last_page ?? 1,
          };
          this.leadExportsLoading = false;
          this.leadExportsLoaded = true;
          this.syncLeadExportsPolling();
        },
        error: () => {
          this.leadExportsLoading = false;
          if (!quiet) {
            this.leadExportsError = 'Could not load your lead exports.';
          }
        }
      });
  }

  onLeadExportsPageChange(page: number): void {
    if (page < 1 || page > this.leadExportsPagination.last_page) {
      return;
    }
    this.loadLeadExports(page);
  }

  onLeadExportContinued(): void {
    this.continuingLeadExport = null;
    this.loadLeadExports(1, true);
  }

  get hasActiveLeadExport(): boolean {
    return this.leadExports.some(exp => isLeadExportActive(exp));
  }

  isLeadExportActive(exp: LeadExport): boolean {
    return isLeadExportActive(exp);
  }

  /** Poll every 5 s while any export on the page is still running. */
  private syncLeadExportsPolling(): void {
    if (this.activeTab === 'leads' && this.hasActiveLeadExport) {
      if (!this.leadExportsPollSub) {
        this.leadExportsPollSub = interval(LEAD_EXPORTS_POLL_MS)
          .pipe(
            takeUntil(this.destroy$),
            switchMap(() => this.prospectingService.listLeadExports(
              this.leadExportsPagination.current_page, this.leadExportsPagination.per_page)),
          )
          .subscribe({
            next: ({ data }) => {
              this.leadExports = data;
              this.syncLeadExportsPolling();
            },
            error: () => { /* next tick retries */ }
          });
      }
    } else {
      this.stopLeadExportsPolling();
    }
  }

  private stopLeadExportsPolling(): void {
    this.leadExportsPollSub?.unsubscribe();
    this.leadExportsPollSub = null;
  }

  downloadLeadExport(exp: LeadExport): void {
    if (!exp.is_downloadable || this.leadExportDownloadingIds.has(exp.id)) {
      return;
    }
    this.leadExportDownloadingIds.add(exp.id);

    this.prospectingService.downloadLeadExport(exp.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          this.leadExportDownloadingIds.delete(exp.id);
          saveBlob(blob, exp.file_name || `leads-export-${exp.id}.xlsx`);
        },
        error: () => {
          this.leadExportDownloadingIds.delete(exp.id);
          this.popupService.notify('Could not download the export.', '', 'error');
        }
      });
  }

  getLeadExportStatusLabel(exp: LeadExport): string {
    switch (exp.status) {
      case 'pending':
        return 'Queued';
      case 'completed_partial':
        return exp.stop_reason === 'insufficient_credits' ? 'Partial (out of credits)' : 'Partial';
      default:
        return this.getStatusLabel(exp.status);
    }
  }

  // ============================================
  // Shared methods
  // ============================================

  getStatusClass(status: string): string {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'completed_partial':
        return 'bg-amber-100 text-amber-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getStatusLabel(status: string): string {
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  getPaginationPages(pagination: ExportListPagination | SearchExportListPagination): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    const current = pagination.current_page;
    const total = pagination.last_page;

    let start = Math.max(1, current - Math.floor(maxVisible / 2));
    const end = Math.min(total, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }
}
